import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import { BRAND } from '../../config/brand';

const Header: React.FC = () => {
  const { currentUser, userProfile, logout } = useAuth();

  const isBidder = userProfile?.role === 'bidder';
  const isSeller = userProfile?.role === 'seller' || userProfile?.role === 'admin' || userProfile?.role === 'consignor';
  // Stricter than isSeller (excludes seller/consignor): only admin/auctioneer see admin-only links.
  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'auctioneer';

  // Consignor portal access is granted by HAVING a consignors/{uid} record (doc id == uid),
  // NOT by role — the real consignors kept their existing 'bidder' role (see App.tsx
  // ConsignorRoute). So a role check would never show them their portal link. We do the same
  // record check the route guard does, and show a "Your Items" link whenever it exists.
  // (Staff without a consignor record won't see it; they reach consignors via /admin/consignors.)
  const [hasConsignorRecord, setHasConsignorRecord] = useState(false);

  useEffect(() => {
    let active = true;
    if (!currentUser) { setHasConsignorRecord(false); return; }
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'consignors', currentUser.uid));
        if (active) setHasConsignorRecord(snap.exists());
      } catch {
        if (active) setHasConsignorRecord(false);
      }
    })();
    return () => { active = false; };
  }, [currentUser]);

  return (
    <header className="bg-white shadow">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-primary-600">
            {BRAND.name}
          </Link>

          {currentUser ? (
            <div className="flex items-center gap-6">
              {/* Consignor portal — shown to anyone with a consignors/{uid} record,
                  independent of role (real consignors are 'bidder'). */}
              {hasConsignorRecord && (
                <Link
                  to="/consignor/dashboard"
                  className="font-semibold text-primary-600 hover:text-primary-700"
                >
                  📦 Your Items
                </Link>
              )}

              {/* Bidder Navigation */}
              {isBidder && (
                <nav className="flex gap-4">
                  <Link to="/browse" className="text-gray-700 hover:text-primary-600 font-medium">
                    Browse Auctions
                  </Link>
                  <Link to="/past" className="text-gray-700 hover:text-primary-600 font-medium">
                    Past Auctions
                  </Link>
                  <Link to="/watching" className="text-gray-700 hover:text-primary-600 font-medium">
                    👀 Watching
                  </Link>
                  <Link to="/my-bids" className="text-gray-700 hover:text-primary-600 font-medium">
                    My Bids
                  </Link>
                  <Link to="/my-invoices" className="text-gray-700 hover:text-primary-600 font-medium">
                    Invoices
                  </Link>
                  <Link to="/profile" className="text-gray-700 hover:text-primary-600 font-medium">
                    Profile
                  </Link>
                </nav>
              )}

              {/* Seller/Admin Navigation */}
              {isSeller && (
                <nav className="flex gap-4">
                  <Link to="/dashboard" className="text-gray-700 hover:text-primary-600 font-medium">
                    Dashboard
                  </Link>
                  <Link to="/auctions" className="text-gray-700 hover:text-primary-600 font-medium">
                    Manage Auctions
                  </Link>
                  {isAdmin && (
                    <Link to="/admin/bidders" className="text-gray-700 hover:text-primary-600 font-medium">
                      Bidders
                    </Link>
                  )}
                  {isAdmin && (
                    <Link to="/admin/bids" className="text-gray-700 hover:text-primary-600 font-medium">
                      Bids
                    </Link>
                  )}
                  <Link to="/import" className="text-gray-700 hover:text-primary-600 font-medium">
                    Import CSV
                  </Link>
                  <Link to="/shipping" className="text-gray-700 hover:text-primary-600 font-medium">
                    📦 Shipping Station
                  </Link>
                </nav>
              )}

              <div className="flex items-center gap-4 border-l pl-4">
                <div className="text-right">
                  {isBidder && (
                    <>
                      <p className="text-sm font-semibold text-gray-800">
                        {userProfile?.displayName || 'Bidder'}
                      </p>
                      <p className="text-xs text-gray-500">
                        #{userProfile?.bidderNumber}
                      </p>
                    </>
                  )}
                  {isSeller && (
                    <p className="text-sm text-gray-600">
                      {userProfile?.auctionHouseName || userProfile?.displayName}
                    </p>
                  )}
                </div>
                <button onClick={logout} className="btn-secondary text-sm">
                  Logout
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <Link to="/browse" className="text-gray-700 hover:text-primary-600 font-medium">
                Browse Auctions
              </Link>
              <Link to="/past" className="text-gray-700 hover:text-primary-600 font-medium">
                Past Auctions
              </Link>
              <Link to="/login" className="btn-secondary text-sm">
                Sign In
              </Link>
              <Link to="/signup" className="btn-primary text-sm">
                Register to Bid
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
